import {useState, useCallback, useEffect, useRef} from 'react';
import classNames from 'classnames';
import { useNavigate, useParams } from 'react-router';
import { useMemoizedFn } from 'ahooks';
import { hasClass } from '@/utils/dom';
import { isSufeExamClient } from '@/utils/examClient';
import { getMockHomeworkSession, isMockHomeworkPreview } from '@/utils/homeworkSession';
import Bus from '@/utils/bus';
import AIContant from '@/utils/aiContant';
import { logCount } from '@/utils/logger/arms-config';
import { recordReviewSearch } from '@/utils/recordReview';
import i18n from '@/locales/init';
import {useAppSelector} from '@/stores/index';
import { useAppDispatch } from '@/stores/index';
// import { useAppDispatch } from '@/stores';
// import { toggleShowNotePanel } from '@/stores/slice/listening';
import {setAudioPlayTime, setPlayIndex, setPlayStatus, setRecordAudioProgress, saveAnswerListAsync as saveListeningDraft} from '@/stores/slice/listening';
import { saveAnswerListAsync as saveReadingDraft } from '@/stores/slice/reading';
import { saveAnswerListAsync as saveWritingDraft } from '@/stores/slice/writing';
import { useModulePoints } from '@/hooks/useModulePoints';
import { SUFE_SUBMIT_RETRY_MS, SUFE_SUBMIT_MAX_RETRY_MS } from '../../examSubmit';
import {AudioSvg, BellSvg, NetSvg, NoteSvg} from '../../Svg/index';
import Audio from '../Audio';
import FinishDialog from '../FinishDialog';
import DisplaySettings from '../ExamDisplay/DisplaySettings';
import { checkHasPoints, checkUserPointsOnly } from '../Points/userPoints';
import { useExamListeningAudio } from './useExamListeningAudio';
import DraftSyncNotice from './DraftSyncNotice';
import style from './index.module.less';

const pointsMap = {
  r: 70,
  l: 69,
  w: 71,
};
const noop = () => undefined;
const draftModule = {l: 'listening', r: 'reading', w: 'writing'} as const;
const saveDraft = {l: saveListeningDraft, r: saveReadingDraft, w: saveWritingDraft};

interface IProps {
  time: number;
  isAI: boolean;
  readOnly?: boolean;
  isSubject: boolean;
  tabSubjectHandle: (val: boolean)=> void;
  isListen: boolean;
  loadAnswered: boolean;
  updateTimeHandle: (val: number) => void;
  submitHandle: (val: () => void) => void | Promise<boolean>;
  noteHandle: () => void;
  audioSrc: string;
  course: 'r' | 'l' | 'w';
  draftSyncedAt?: number;
}

export default function TopBar(props: IProps) {
  const {time: timeProp, isAI, readOnly = false, isSubject, tabSubjectHandle, isListen, loadAnswered, updateTimeHandle, submitHandle, noteHandle, audioSrc, course} = props;
  const preview = isMockHomeworkPreview();
  const teacherTrial = getMockHomeworkSession()?.mode === 'teacher_sandbox';
  const automaticSubmissionOnly = !isAI && isSufeExamClient();
  const retryTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const retryDelayRef = useRef(SUFE_SUBMIT_RETRY_MS);
  const mountedRef = useRef(false);
  const submissionContextRef = useRef(0);
  const autoSubmissionStartedRef = useRef(false);
  const autoSubmissionInFlightRef = useRef(false);
  const autoSubmissionConfirmedRef = useRef(false);
  const autoSubmissionAuthorizedRef = useRef(false);
  const submissionBlockedRef = useRef(false);
  const submitHandleRef = useRef(submitHandle);
  submitHandleRef.current = submitHandle;

  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.userId);
  const { recordId } = useParams();
  const pointsInit = useAppSelector(state => state.points.initialized);

  const navigate = useNavigate();
  // const showNotePanel = () => {
  //   dispatch(toggleShowNotePanel());
  // };

  const {audioIndex, audioSrcArr, audioStartPlayTime, recordAudioProgressArr, pageDataIndex} = useAppSelector(state=> state.listening);
  const draft = useAppSelector(state => state[draftModule[course]]);

  const [show, setShow] = useState(false);
  const [audioSuspendedForSubmission, setAudioSuspendedForSubmission] = useState(false);
  const [autoSubmissionStatus, setAutoSubmissionStatus] = useState<'submitting' | 'retrying' | 'offline' | 'points' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const autoSubmitOverlayRef = useRef<HTMLDivElement | null>(null);

  const cancelHandle = useCallback(()=> {
    setShow(false);
  }, []);

  const showDialogHandle = useCallback(()=> {
    if (automaticSubmissionOnly) return;
    logCount('Click_Finish_Button');
    setShow(true);
  }, [automaticSubmissionOnly]);

  const time = useRef(timeProp);
  const timeRef = useRef<HTMLElement | null>(null);
  const hoverRef = useRef<HTMLElement | null>(null);
  const timeBoxRef = useRef<HTMLDivElement | null>(null);
  const pointsInfo = useModulePoints(pointsMap[course] || 0);

  const clearRetryTimer = useCallback(() => {
    clearTimeout(retryTimer.current);
    retryTimer.current = undefined;
  }, []);

  const isCurrentSubmissionContext = useCallback((context: number) => (
    mountedRef.current && submissionContextRef.current === context
  ), []);

  const stopListeningAudio = useCallback(() => {
    submissionBlockedRef.current = true;
    audioRef.current?.pause();
    if (isListen) {
      dispatch(setPlayStatus('pause'));
    }
  }, [dispatch, isListen]);

  const beginAutomaticSubmission = useMemoizedFn(() => {
    if (isAI || !autoSubmissionStartedRef.current || autoSubmissionConfirmedRef.current || time.current > 0 || autoSubmissionInFlightRef.current) {
      return;
    }

    const context = submissionContextRef.current;
    if (!isCurrentSubmissionContext(context)) {
      return;
    }

    clearRetryTimer();
    if (!automaticSubmissionOnly && !autoSubmissionAuthorizedRef.current) {
      if (!pointsInit) {
        scheduleAutomaticRetry(context);
        return;
      }
      if (!checkUserPointsOnly(pointsInfo.modulePoints)) {
        scheduleAutomaticRetry(context);
        setAutoSubmissionStatus('points');
        return;
      }
      // Retry an uncertain submission without repeatedly debiting local points.
      // The API remains responsible for charging and reconciling the result.
      autoSubmissionAuthorizedRef.current = true;
    }
    autoSubmissionInFlightRef.current = true;
    setAutoSubmissionStatus(navigator.onLine === false ? 'offline' : 'submitting');

    let pending: void | Promise<boolean>;
    try {
      pending = submitHandleRef.current(noop);
    } catch {
      pending = Promise.reject(new Error('automatic submission failed'));
    }

    Promise.resolve(pending).then(succeeded => {
      if (!isCurrentSubmissionContext(context)) {
        return;
      }
      autoSubmissionInFlightRef.current = false;
      if (succeeded) {
        autoSubmissionConfirmedRef.current = true;
        clearRetryTimer();
        return;
      }
      scheduleAutomaticRetry(context);
    }).catch(() => {
      if (!isCurrentSubmissionContext(context)) {
        return;
      }
      autoSubmissionInFlightRef.current = false;
      scheduleAutomaticRetry(context);
    });
  });

  const scheduleAutomaticRetry = useMemoizedFn((context: number) => {
    if (!isCurrentSubmissionContext(context) || autoSubmissionConfirmedRef.current || retryTimer.current || autoSubmissionInFlightRef.current) {
      return;
    }

    // onLine is only a hint in the desktop client. Keep probing even when an
    // online event is missing or the operating system reports stale connectivity.
    setAutoSubmissionStatus(navigator.onLine === false ? 'offline' : 'retrying');
    retryTimer.current = setTimeout(() => {
      retryTimer.current = undefined;
      if (isCurrentSubmissionContext(context)) {
        beginAutomaticSubmission();
      }
    }, retryDelayRef.current);
    retryDelayRef.current = Math.min(retryDelayRef.current * 2, SUFE_SUBMIT_MAX_RETRY_MS);
  });

  const startAutomaticSubmission = useMemoizedFn(() => {
    if (isAI || autoSubmissionStartedRef.current) {
      return;
    }
    autoSubmissionStartedRef.current = true;
    setAutoSubmissionStatus('submitting');
    stopListeningAudio();
    beginAutomaticSubmission();
  });

  const autoSubmit = useMemoizedFn(async (cb: () => void) => {
    if (time.current <= 0) {
      if (!pointsInit) return;
      if (!autoSubmissionAuthorizedRef.current && !checkHasPoints(pointsInfo.modulePoints, () => {
        beginAutomaticSubmission();
      }, {checkOnly: true})) return;
      autoSubmissionAuthorizedRef.current = true;
      cb();
      startAutomaticSubmission();
      beginAutomaticSubmission();
      return;
    }
    if (!checkHasPoints(pointsInfo.modulePoints)) {
      return;
    }
    const context = submissionContextRef.current;
    stopListeningAudio();
    setAudioSuspendedForSubmission(true);
    let succeeded = false;
    try {
      succeeded = await submitHandle(cb) === true;
    } catch {
      // The submission flow reports failures; keep audio recoverable if there
      // is still exam time remaining.
    } finally {
      if (!succeeded && time.current > 0 && isCurrentSubmissionContext(context)) {
        submissionBlockedRef.current = false;
        setAudioSuspendedForSubmission(false);
      }
    }
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearRetryTimer();
    };
  }, [clearRetryTimer]);

  useEffect(() => {
    submissionContextRef.current += 1;
    autoSubmissionStartedRef.current = false;
    autoSubmissionInFlightRef.current = false;
    autoSubmissionConfirmedRef.current = false;
    autoSubmissionAuthorizedRef.current = false;
    submissionBlockedRef.current = false;
    retryDelayRef.current = SUFE_SUBMIT_RETRY_MS;
    clearRetryTimer();
    setAutoSubmissionStatus(null);
    setAudioSuspendedForSubmission(false);
  }, [course, recordId, clearRetryTimer]);

  useEffect(() => {
    const overlay = autoSubmitOverlayRef.current;
    if (!automaticSubmissionOnly || !autoSubmissionStatus || !overlay) return;
    overlay.focus();
    const retainFocus = (event: FocusEvent) => {
      if (!overlay.contains(event.target as Node)) overlay.focus();
    };
    const preventTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();
        overlay.focus();
      }
    };
    document.addEventListener('focusin', retainFocus);
    document.addEventListener('keydown', preventTab, true);
    return () => {
      document.removeEventListener('focusin', retainFocus);
      document.removeEventListener('keydown', preventTab, true);
    };
  }, [automaticSubmissionOnly, autoSubmissionStatus]);

  useEffect(() => {
    if (isAI) {
      return;
    }

    const handleOffline = () => {
      if (autoSubmissionStartedRef.current && !autoSubmissionConfirmedRef.current && time.current <= 0) {
        clearRetryTimer();
        setAutoSubmissionStatus('offline');
        scheduleAutomaticRetry(submissionContextRef.current);
      }
    };
    const handleOnline = () => {
      if (autoSubmissionStartedRef.current && !autoSubmissionConfirmedRef.current && time.current <= 0) {
        clearRetryTimer();
        retryDelayRef.current = SUFE_SUBMIT_RETRY_MS;
        beginAutomaticSubmission();
      }
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAI, beginAutomaticSubmission, clearRetryTimer]);
  
  useEffect(() => {
    let blinkCount = 0;
    let startBlink = false;
    function convertSeconds(seconds: number) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      // 开启开关
      if (minutes === 10 && remainingSeconds === 0) {
        startBlink = true;
      }

      if ( startBlink && blinkCount <= 12) {
        if (blinkCount % 2 === 1) {
          if (timeBoxRef.current) {
            timeBoxRef.current.classList.add(style.red);
          }
        } else {
          if (timeBoxRef.current) {
            timeBoxRef.current.classList.remove(style.red);
          }
        }
        blinkCount++;
      }

      if (seconds <= 300) {
        if (timeBoxRef.current) {
          if (!hasClass(timeBoxRef.current, style.red)) {
            timeBoxRef.current.classList.add(style.red);
          }
        }
        
      }

      if (timeBoxRef.current && seconds < 90) {
        timeBoxRef.current.classList.add(style.showSecond);
      }

      
      let remainingSecondsStr = String(remainingSeconds);
      if (remainingSeconds < 10) {
        remainingSecondsStr = '0' + remainingSeconds;
      }

      if (timeRef.current && hoverRef.current) {
        timeRef.current.textContent = (remainingSeconds === 0 ? minutes : minutes + 1) + ' minutes remaining';
        hoverRef.current.textContent = `${minutes}:${remainingSecondsStr} remaining`;
      }
    }
    let timer:number |NodeJS.Timeout;
    if (loadAnswered && !isAI) {
      time.current = timeProp;
      if (time.current <= 0) stopListeningAudio();
      let lastTick = Date.now();
      timer = setInterval(() => {
        const now = Date.now();
        time.current -= (now - lastTick) / 1000;
        updateTimeHandle(Math.floor(time.current));
        lastTick = now;
        if (time.current <= 0) stopListeningAudio();
        if (time.current <= 0) {
          clearInterval(timer);
          startAutomaticSubmission();
          return;
        }
        convertSeconds(Math.max(time.current, 0));
      }, 1000);
    }
    
    return () => {
      if (!isAI) {
        clearInterval(timer);
      }
    };
  }, [loadAnswered, isListen, isAI, startAutomaticSubmission, stopListeningAudio]);

  const listeningAudio = useExamListeningAudio({
    audioRef,
    active: !isAI && isListen && loadAnswered && !audioSuspendedForSubmission && !!recordId && !!userId,
    examKey: `${userId}:${recordId}`,
    sources: audioSrcArr,
    audioIndex,
    startTime: audioStartPlayTime,
    blockedRef: submissionBlockedRef,
    onIndexChange: index => dispatch(setPlayIndex(index)),
    onTimeChange: seconds => dispatch(setAudioPlayTime(seconds)),
  });
  const canRetryAudio = ['error', 'offline', 'blocked'].includes(listeningAudio.status) && !submissionBlockedRef.current;
  const retryDraftSync = useMemoizedFn(() => {
    if (!isAI && loadAnswered && time.current > 0 && !submissionBlockedRef.current && draft.recordId === Number(recordId)) {
      dispatch(saveDraft[course]({recordId: Number(recordId)}));
    }
  });

  const backHandle = useCallback(() => {
    navigate(`/mockstart/${recordId}${recordReviewSearch(window.location.hash.split('?')[1] || '')}`);
  }, [navigate, recordId]);

  const playStateCallBack = (val: boolean) => {
    dispatch(setPlayStatus(val ? 'play' : 'pause'));
    if (isAI && !val) {
      Bus.publish(AIContant.AUDIO_STOP, undefined);
    }
  };

  const saveRecordProgress = (val: {duration: number; currentTime: number;}) => {
    dispatch(setRecordAudioProgress(val));
  };
  
  return (
    <div className={style.topBar} data-exam-toolbar>
      <div className={style.topLeft}>
        <div className={style.info}>
          <div className={style.studentId}>{preview ? '教师预览' : teacherTrial ? '教师试做' : i18n.t('common.dauyan_zkn_xxxxxx', { user_id: userId })}</div>
          {
            isAI ? <div className={style.timeBox}>
              {readOnly ? <span role="status">学生学习记录 · 只读查看</span> : <>&nbsp;</>}
            </div> : <div className={style.timeBox} ref={timeBoxRef}>
              <span className={style.time} ref={timeRef}></span>
              <span className={style.minutes} ref={hoverRef}></span>
            </div>
          }
        </div>
        
        {
          !isAI && isListen ? <div className={style.audioBox}>
            <audio id="audio" ref={audioRef}></audio>
            <div className={style.audioStatusBox}>
              <i className={style.audioIcon}>{AudioSvg}</i>
              <span className={style.audioStatus} aria-live="polite">{i18n.t(`common.dauyan_exam_audio_${listeningAudio.status}`)}</span>
              {canRetryAudio && <button type="button" className={style.audioRetry} onClick={listeningAudio.retry}>
                {i18n.t(listeningAudio.status === 'blocked' ? 'common.dauyan_confirm' : 'common.dauyan_exam_audio_retry')}
              </button>}
            
            </div>
          </div> : null
        }

        {
          isAI && isListen ? <div className={style.tabList}>
            <div className={classNames(style.tabTitle, isSubject ? style.active: '')} onClick={() => tabSubjectHandle(true)}>{preview ? '题目' : i18n.t('common.dauyan_following_search_result_v')}</div>
            <div className={classNames(style.tabTitle, isSubject ? '' : style.active)} onClick={() => tabSubjectHandle(false)}>{i18n.t('common.dauyan_feedback_origin_essay')}</div>
          </div>
            :
            null
        }
        {
          isAI && isListen ? 
            <Audio src={audioSrc} playStateCallBack={playStateCallBack} recordAudioProgress={recordAudioProgressArr[pageDataIndex]} saveRecordProgress={saveRecordProgress}></Audio> : null
        } 
      </div>
      <div className={style.topRight}>
        
        { isAI ? <button className={style.button} onClick={backHandle}>{i18n.t('common.dauyan_return')}</button> : <button className={style.button} disabled={automaticSubmissionOnly} onClick={showDialogHandle}>{automaticSubmissionOnly ? i18n.t('common.dauyan_exam_auto_submit') : 'Finish test'}</button> }
        <i className={style.icon}>{NetSvg}</i>
        <i className={style.icon}>{BellSvg}</i>
        <DisplaySettings />
        <i className={style.icon} onClick={noteHandle}>{NoteSvg}</i>
      </div>
      <DraftSyncNotice key={`${course}:${recordId}`}
        active={!isAI && loadAnswered && timeProp > 0 && !submissionBlockedRef.current && draft.recordId === Number(recordId)}
        syncedAt={draft.draftSyncedAt}
        failureCount={draft.draftSaveHealth.recordId === Number(recordId) ? draft.draftSaveHealth.failures : 0}
        lastFailure={draft.draftSaveHealth.recordId === Number(recordId) ? draft.draftSaveHealth.lastFailure : undefined}
        pending={draft.draftSavePending}
        onRetry={retryDraftSync} />
      {!automaticSubmissionOnly && <FinishDialog course={course} show={show} cancel={cancelHandle} submitHandle={autoSubmit}></FinishDialog>}
      {autoSubmissionStatus && <div className={automaticSubmissionOnly ? style.autoSubmitOverlay : style.autoSubmitNotice} ref={autoSubmitOverlayRef} role="status" aria-live="assertive" aria-busy={autoSubmissionStatus !== 'points'} tabIndex={-1}>
        <div className={style.autoSubmitMessage}>
          {autoSubmissionStatus === 'submitting' && i18n.t('common.dauyan_exam_submitting')}
          {autoSubmissionStatus === 'retrying' && i18n.t('common.dauyan_exam_submit_retrying')}
          {autoSubmissionStatus === 'offline' && i18n.t('common.dauyan_exam_submit_offline')}
          {autoSubmissionStatus === 'points' && i18n.t('common.dauyan_exam_submit_points')}
        </div>
      </div>}
    </div>
  );
}
